import { use, useCallback, useId, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Drawer from '@mui/material/Drawer';
import LinearProgress from '@mui/material/LinearProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import { ConfirmContext, type ConfirmOptions } from '@/components/feedback/confirmContext';
import CustomScrollbar from '@/components/common/CustomScrollbar';
import { OverlayHeader } from './OverlayHeader';
import { OverlayContext, type OverlayContextValue } from './overlayContext';
import {
  DIALOG_WIDTHS,
  DRAWER_WIDTHS,
  type OverlayCloseReason,
  type OverlayShellProps,
  type OverlayVariant,
} from './overlay.types';

export interface AppOverlayProps extends OverlayShellProps {
  variant: OverlayVariant;
}

const DISCARD: ConfirmOptions = {
  title: 'Discard changes?',
  description: 'Your unsaved changes will be lost.',
  confirmLabel: 'Discard',
  cancelLabel: 'Keep editing',
  tone: 'error',
};

/**
 * Foundation of every overlay: one layout (header · scrolling body · footer),
 * one set of close rules and one accessibility contract, rendered as a
 * centred dialog or a right-hand drawer.
 *
 * Close rules, in order: nothing closes while `busy`; backdrop / Escape only
 * when `dismissible`; `confirmClose` asks first. Pages normally use AppDialog,
 * AppDrawer or one of the purpose-built overlays instead of this directly.
 */
export function AppOverlay({
  variant,
  open,
  onClose,
  title,
  subtitle,
  icon,
  headerActions,
  size = variant === 'dialog' ? 'sm' : 'md',
  busy = false,
  dismissible = true,
  confirmClose = false,
  footer,
  onSubmit,
  disablePadding = false,
  disableBodyScroll = false,
  bodySx,
  onExited,
  children,
}: AppOverlayProps) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const confirm = use(ConfirmContext);
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const subtitleId = `${baseId}-subtitle`;
  const confirming = useRef(false);

  const requestClose = useCallback(
    (reason: OverlayCloseReason) => {
      if (busy || confirming.current) return;
      if (!dismissible && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
      if (!confirmClose || !confirm) {
        onClose();
        return;
      }
      confirming.current = true;
      void confirm(confirmClose === true ? DISCARD : { ...DISCARD, ...confirmClose })
        .then((ok) => {
          if (ok) onClose();
        })
        .finally(() => {
          confirming.current = false;
        });
    },
    [busy, dismissible, confirmClose, confirm, onClose],
  );

  const context = useMemo<OverlayContextValue>(
    () => ({ variant, titleId, subtitleId, busy, requestClose }),
    [variant, titleId, subtitleId, busy, requestClose],
  );

  const content = (
    <OverlayContext value={context}>
      <Box
        component={onSubmit ? 'form' : 'div'}
        onSubmit={onSubmit}
        noValidate={onSubmit ? true : undefined}
        sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, position: 'relative' }}
      >
        <OverlayHeader title={title} subtitle={subtitle} icon={icon} actions={headerActions} />
        {busy ? (
          <LinearProgress sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: 2 }} />
        ) : null}
        {disableBodyScroll ? (
          <Box
            sx={
              [
                { flex: '1 1 auto', minHeight: 0, p: disablePadding ? 0 : { xs: 2, sm: 3 } },
                ...(Array.isArray(bodySx) ? (bodySx as readonly unknown[]) : [bodySx]),
              ] as SxProps<Theme>
            }
          >
            {children}
          </Box>
        ) : (
          // The app's scrollbar rather than the browser's, as in the sidebar.
          <CustomScrollbar noPadding style={{ flex: '1 1 auto', minHeight: 0 }}>
            <Box
              sx={
                [
                  { p: disablePadding ? 0 : { xs: 2, sm: 3 } },
                  ...(Array.isArray(bodySx) ? (bodySx as readonly unknown[]) : [bodySx]),
                ] as SxProps<Theme>
              }
            >
              {children}
            </Box>
          </CustomScrollbar>
        )}
        {footer}
      </Box>
    </OverlayContext>
  );

  const aria = {
    'aria-labelledby': titleId,
    'aria-describedby': subtitle ? subtitleId : undefined,
  };

  if (variant === 'drawer') {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={(_, reason) => requestClose(reason)}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-modal': true,
            ...aria,
            sx: {
              width: compact ? '100%' : DRAWER_WIDTHS[size],
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
              // The theme's Paper border would show as a light line around the coloured header.
              border: 'none',
            },
          },
          transition: { onExited },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => requestClose(reason)}
      fullScreen={compact}
      maxWidth={false}
      {...aria}
      slotProps={{
        paper: { sx: { width: '100%', maxWidth: compact ? '100%' : DIALOG_WIDTHS[size] } },
        transition: { onExited },
      }}
    >
      {content}
    </Dialog>
  );
}
